import { useCallback } from 'react';
import { FullProject, ProjectMetadata, Task, LogEntry } from '../../types';

interface UseProjectExportsProps {
  activeProject: FullProject | undefined;
  allProjects: FullProject[];
  projectName: string;
  projectMetadata: ProjectMetadata;
  tasks: Task[];
  addLogEntry: (action: LogEntry['action'], description: string) => void;
}

export function useProjectExports({
  activeProject,
  allProjects,
  projectName,
  projectMetadata,
  tasks,
  addLogEntry
}: UseProjectExportsProps) {

  const exportAsJSON = useCallback(() => {
    const data = JSON.stringify({
      version: 3,
      exportedAt: new Date().toISOString(),
      allProjects
    }, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `stratum_full_backup_${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    addLogEntry('import', 'Vollständiges System-Backup als JSON exportiert');
  }, [allProjects, addLogEntry]);

  const exportAsCSV = useCallback(() => {
    const currentProject = activeProject;
    const pName = currentProject?.name || 'Unbekanntes_Projekt';
    const tasksToExport = tasks;

    const metadataFields = [
      { key: 'vision', label: 'Vision' },
      { key: 'goals', label: 'Ziele' },
      { key: 'stakeholders', label: 'Stakeholder' },
      { key: 'theoryOfChange', label: 'Theory of Change' },
      { key: 'participation', label: 'Partizipation' },
      { key: 'resources', label: 'Ressourcen' },
      { key: 'budget', label: 'Budget' },
      { key: 'risks', label: 'Risiken' },
      { key: 'description', label: 'Beschreibung (Konzept)' },
      { key: 'communication', label: 'Kommunikation' },
      { key: 'sustainability', label: 'Nachhaltigkeit' },
      { key: 'kpis', label: 'KPIs' }
    ];

    const headers = [
      'Projekt', 'ID', 'Vorgang', 'Typ', 'Status', 'Start', 'Ende', 'Startzeit', 'Endzeit', 'Beschreibung', 'Zuständig', 'Abhängigkeiten',
      ...metadataFields.map(f => f.label)
    ];

    const csvContent = '\uFEFF' + [
      headers.join(';'),
      ...tasksToExport.map(t => {
        const typeStr = t.type || 'task';
        const statusStr = t.completed ? 'completed' : 'open';
        const name = `"${(t.name || '').replace(/"/g, '""')}"`;
        const assignee = `"${(t.assignee || '').replace(/"/g, '""')}"`;
        const startTime = t.startTime || '';
        const endTime = t.endTime || '';
        const notesRaw = t.notes;
        const notesText = Array.isArray(notesRaw)
          ? notesRaw.map(n => n.text).join(' | ')
          : (notesRaw || '');
        const description = `"${notesText.replace(/"/g, '""')}"`;
        const deps = `"${(t.dependencies || []).join(', ')}"`;
        const projectNameEscaped = `"${pName.replace(/"/g, '""')}"`;

        const metaValues = metadataFields.map(field => {
          const val = (projectMetadata as Record<string, unknown>)[field.key] || '';
          return `"${val.toString().replace(/"/g, '""').replace(/\n/g, ' ')}"`;
        });

        return [
          projectNameEscaped, t.id, name, typeStr, statusStr, t.start, t.end, startTime, endTime, description, assignee, deps,
          ...metaValues
        ].join(';');
      })
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${pName.replace(/\s+/g, '_')}_export.csv`;
    link.click();
    addLogEntry('import', `Projekt "${pName}" als CSV exportiert`);
  }, [activeProject, tasks, projectMetadata, addLogEntry]);

  const exportAsExcel = useCallback(async () => {
    try {
      const { runExportAsExcel } = await import('./exports/excelExports');
      await runExportAsExcel(activeProject, tasks, projectMetadata, addLogEntry);
    } catch (error) {
      console.error('Excel export failed:', error);
      alert('Fehler beim Excel-Export.');
    }
  }, [activeProject, tasks, projectMetadata, addLogEntry]);

  const exportTaskTableAsExcel = useCallback(async () => {
    const { runExportTaskTableAsExcel } = await import('./exports/excelExports');
    await runExportTaskTableAsExcel(activeProject, tasks, allProjects);
  }, [activeProject, tasks, allProjects]);

  const exportKanbanColumnAsExcel = useCallback(async (
    status: string,
    columnTitle: string,
    isGlobalOrTasks: boolean | Task[] = false,
    columnTasksOverride?: Task[]
  ) => {
    const { runExportKanbanColumnAsExcel } = await import('./exports/excelExports');
    await runExportKanbanColumnAsExcel(activeProject, tasks, allProjects, status, columnTitle, isGlobalOrTasks, columnTasksOverride);
  }, [activeProject, tasks, allProjects]);

  const exportContactsAsExcel = useCallback(async (isGlobal: boolean = false) => {
    const { runExportContactsAsExcel } = await import('./exports/excelExports');
    await runExportContactsAsExcel(activeProject, allProjects, isGlobal);
  }, [activeProject, allProjects]);

  const handleDownloadPDF = useCallback(async () => {
    const { runDownloadPDF } = await import('./exports/pdfRoadmapExport');
    await runDownloadPDF(projectName, tasks, addLogEntry);
  }, [projectName, tasks, addLogEntry]);

  const handleDownloadRoadmap = useCallback(async (options: {
    includeMilestones?: boolean;
    includePhases?: boolean;
    includeMeetings?: boolean;
    includeTasks?: boolean;
    includeSubtasks?: boolean;
    showTodayLine?: boolean;
    startDate?: string;
    endDate?: string;
    minPriority?: string;
    onlyCompleted?: boolean;
    hideCompleted?: boolean;
    projectName?: string;
    tasks?: Task[];
  } = {}) => {
    const { runDownloadRoadmap } = await import('./exports/pdfRoadmapExport');
    await runDownloadRoadmap(projectName, tasks, addLogEntry, options);
  }, [projectName, tasks, addLogEntry]);

  const exportAsWord = useCallback(async (type: 'concept' | 'report') => {
    try {
      const [{ saveAs }, { Paragraph, HeadingLevel, AlignmentType, Document, Packer }] = await Promise.all([
        import('file-saver'),
        import('docx'),
      ]);

      const content = type === 'concept' ? projectMetadata.generatedConcept : projectMetadata.report;
      if (!content) {
        alert('Bitte generiere zuerst den Inhalt per KI.');
        return;
      }

      const pName = projectName || 'Projekt';
      const title = type === 'concept' ? `Projektkonzept: ${pName}` : `Projektbericht: ${pName}`;

      const lines = content.split('\n');
      const docElements: (InstanceType<typeof Paragraph>)[] = [
        new Paragraph({
          text: title,
          heading: HeadingLevel.HEADING_1,
          alignment: AlignmentType.CENTER,
          spacing: { after: 400 }
        })
      ];

      lines.forEach(line => {
        if (line.startsWith('### ')) {
          docElements.push(new Paragraph({ text: line.replace('### ', ''), heading: HeadingLevel.HEADING_3, spacing: { before: 200, after: 100 } }));
        } else if (line.startsWith('## ')) {
          docElements.push(new Paragraph({ text: line.replace('## ', ''), heading: HeadingLevel.HEADING_2, spacing: { before: 300, after: 150 } }));
        } else if (line.startsWith('# ')) {
          docElements.push(new Paragraph({ text: line.replace('# ', ''), heading: HeadingLevel.HEADING_1, spacing: { before: 400, after: 200 } }));
        } else if (line.trim()) {
          docElements.push(new Paragraph({ text: line, spacing: { after: 120 } }));
        }
      });

      const doc = new Document({
        sections: [{
          properties: {},
          children: docElements
        }]
      });

      const blob = await Packer.toBlob(doc);
      saveAs(blob, `${pName.replace(/\s+/g, '_')}_${type}.docx`);
      addLogEntry('import', `Word-Export (${type}) für "${pName}" heruntergeladen`);
    } catch (error) {
      console.error('Word export failed:', error);
      alert('Fehler beim Word-Export.');
    }
  }, [projectName, projectMetadata, addLogEntry]);

  return {
    exportAsJSON,
    exportAsCSV,
    exportAsExcel,
    exportTaskTableAsExcel,
    exportKanbanColumnAsExcel,
    exportContactsAsExcel,
    handleDownloadPDF,
    handleDownloadRoadmap,
    exportAsWord
  };
}
